import {
  Copy,
  ExternalLink,
  HelpCircle,
  Loader2,
  Plus,
  Save,
  Wand2,
  X,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { useOrgNavigate } from '@/hooks/use-org-navigate';

import { BearersPanel } from '@/components/agents/bearers-panel';
import { PendingChangesFooter } from '@/components/pending-changes-footer';
import { IntegrationSnippets } from '@/pages/agents/integration-snippets';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  MultiSelectAutocomplete,
  type MultiSelectAutocompleteOption,
} from '@/components/ui/multi-select-autocomplete';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { ROUTES } from '@/constants/routes';
import {
  autoGenerateAgentDescription,
  createAgent,
  getAgentDetail,
  updateAgent,
} from '@/services/agents/config';
import {
  getAdminCatalog,
  type CatalogItem,
} from '@/services/admin/get-admin-catalog';
import { getOrgNodeAccessConfig } from '@/services/admin/get-org-node-access-config';
import { getCollections } from '@/services/data-sources/get-collections';
import { getWorkflow } from '@/services/workflow/get-workflow';
import { getWorkflowList } from '@/services/workflow/get-workflow-list';
import { useOrgStore } from '@/stores/org-store';
import {
  AGENT_MODELS,
  type Agent,
  type AgentCreateRequest,
  type WorkflowSummaryForDescription,
} from '@/types/agent-types';
import type { Collection } from '@/types/source-types';
import type { WorkflowDetails } from '@/types/workflow-types';
import {
  type AgentFormSnapshot,
  INITIAL_FORM,
  cleanedFormSnapshot,
  countChangedFields,
  snapshotFromAgent,
} from '@/pages/agents/helpers/agent-form-snapshot';

export function AgentConfigPage() {
  const { agentId } = useParams<{ agentId: string }>();
  const navigate = useOrgNavigate();
  const currentOrg = useOrgStore((s) => s.currentOrg);

  const isNew = !agentId || agentId === 'new';

  const [loadedAgent, setLoadedAgent] = useState<Agent | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [form, setForm] = useState<AgentFormSnapshot>(INITIAL_FORM);
  const {
    name,
    description,
    modelName,
    modelProvider,
    systemPrompt,
    maxTokens,
    temperature,
    topP,
    thinkingEnabled,
    thinkingBudgetTokens,
    enableWorkflowRuntime,
    enableWorkflowAuthoring,
    enableA2AOutbound,
    enableScheduling,
    exposedViaA2A,
    renderFilesInChat,
    showIntermediateFiles,
    allowUserWorkflows,
    enableGithubTools,
    memoryEnabled,
    memoryRetentionDays,
    enableTaskAlignmentGuardrail,
    taskAlignmentMinRelevance,
    e2bEnabled,
    e2bTemplate,
    e2bRunTimeoutS,
    e2bMaxEditCalls,
    e2bAllowInternet,
    e2bSandboxEnv,
    refuseSyntheticDataGeneration,
    workflowAllowlist,
    workflowAllowlistLabels,
    mcpToolAllowlist,
    dataCollectionAllowlist,
  } = form;
  const update = useCallback(
    <K extends keyof AgentFormSnapshot>(
      key: K,
      value:
        | AgentFormSnapshot[K]
        | ((prev: AgentFormSnapshot[K]) => AgentFormSnapshot[K]),
    ) =>
      setForm((f) => ({
        ...f,
        [key]:
          typeof value === 'function'
            ? (value as (p: AgentFormSnapshot[K]) => AgentFormSnapshot[K])(
                f[key],
              )
            : value,
      })),
    [],
  );

  const [orgWorkflows, setOrgWorkflows] = useState<WorkflowDetails[]>([]);

  const [adminCatalog, setAdminCatalog] = useState<CatalogItem[]>([]);
  const [orgNodeAccess, setOrgNodeAccess] = useState<
    Record<string, { allowed_by_capitol: boolean; enabled_by_org: boolean }>
  >({});

  const [orgCollections, setOrgCollections] = useState<Collection[]>([]);

  // ``revealedBearer`` carries the first-issuance bearer that
  // ``createAgent`` returns once on agent creation -- used to populate
  // the IntegrationSnippets bearer placeholder. The multi-bearer
  // post-creation flow lives entirely inside ``BearersPanel``.
  const [revealedBearer, setRevealedBearer] = useState<string | null>(null);
  const [a2aEndpointUrl, setA2aEndpointUrl] = useState<string>('');
  const [a2aAgentCardUrl, setA2aAgentCardUrl] = useState<string>('');

  const [isGeneratingDescription, setIsGeneratingDescription] = useState(false);
  const [generateDescriptionError, setGenerateDescriptionError] = useState<
    string | null
  >(null);
  // "Describe from workflows" toggle next to the auto-generate button.
  // When on, the handler fetches each allowlisted workflow's full payload,
  // derives node kinds + input kinds, and forwards the richer
  // ``workflow_summaries`` field to the description generator so the LLM
  // can describe what the agent can DO with the workflows rather than
  // just naming them.
  const [describeFromWorkflows, setDescribeFromWorkflows] = useState(false);

  const [activeTab, setActiveTab] = useState('configuration');
  const [savedSnapshot, setSavedSnapshot] = useState<AgentFormSnapshot | null>(
    null,
  );

  const applySnapshot = useCallback((s: AgentFormSnapshot) => setForm(s), []);

  useEffect(() => {
    if (isNew || !currentOrg?.orgid || !agentId) return;
    let cancelled = false;
    const load = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const detail = await getAgentDetail(currentOrg.orgid, agentId);
        if (cancelled) return;
        const agent = detail?.agent;
        if (!agent) {
          setError(`Agent ${agentId} not found`);
          return;
        }
        setLoadedAgent(agent);
        if (detail?.a2a_endpoint_url)
          setA2aEndpointUrl(detail.a2a_endpoint_url);
        if (detail?.a2a_agent_card_url)
          setA2aAgentCardUrl(detail.a2a_agent_card_url);
        const snapshot = snapshotFromAgent(agent);
        applySnapshot(snapshot);
        setSavedSnapshot(snapshot);
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Failed to load agent');
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [isNew, currentOrg?.orgid, agentId, applySnapshot]);

  useEffect(() => {
    if (!currentOrg?.orgid) return;
    const ctrl = new AbortController();
    (async () => {
      try {
        const resp = await getWorkflowList(currentOrg.orgid, ctrl.signal);
        const list = resp?.data?.workflows ?? [];
        setOrgWorkflows(list);
      } catch {
        /* non-fatal: allowlists degrade to free-text */
      }
    })();
    return () => ctrl.abort();
  }, [currentOrg?.orgid]);

  useEffect(() => {
    if (!currentOrg?.orgid) return;
    let cancelled = false;
    (async () => {
      try {
        const [catalog, access] = await Promise.all([
          getAdminCatalog(),
          getOrgNodeAccessConfig(currentOrg.orgid),
        ]);
        if (cancelled) return;
        setAdminCatalog(catalog);
        setOrgNodeAccess(access.items || {});
      } catch {
        /* ignore - allowlist becomes free-text-only */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentOrg?.orgid]);

  useEffect(() => {
    if (!currentOrg?.orgid) return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await getCollections();
        if (cancelled) return;
        if (Array.isArray(resp)) setOrgCollections(resp);
      } catch {
        /* non-fatal: allowlist degrades to free-text */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentOrg?.orgid]);

  const currentSnapshot = form;

  const changesCount = useMemo(
    () =>
      savedSnapshot ? countChangedFields(currentSnapshot, savedSnapshot) : 0,
    [currentSnapshot, savedSnapshot],
  );

  const handleDiscard = () => {
    if (savedSnapshot) applySnapshot(savedSnapshot);
  };

  const currentModelMeta = useMemo(
    () => AGENT_MODELS.find((m) => m.value === modelName) ?? AGENT_MODELS[0],
    [modelName],
  );
  const currentModelMaxOutput = currentModelMeta.maxOutputTokens;
  const currentModelSupportsThinking = currentModelMeta.supportsThinking;

  // Only workflows with the "publish to API" flag on are actually invokable by
  // an agent over the A2A gateway, so we filter the autocomplete catalog to
  // that subset. Anything else in the org list would be a dead pick that
  // silently fails at runtime.
  const apiPublishedWorkflows = useMemo(
    () => orgWorkflows.filter((w) => w.publish_to_api === true),
    [orgWorkflows],
  );

  const workflowIdOptions = useMemo<MultiSelectAutocompleteOption[]>(
    () =>
      apiPublishedWorkflows.map((w) => ({
        value: w.workflow_id,
        label: w.name || w.workflow_id,
        description: w.description || undefined,
      })),
    [apiPublishedWorkflows],
  );

  // Same constraint as workflow ids: a label can only resolve to a runnable
  // workflow via the A2A gateway if at least one workflow carrying that label
  // is publish_to_api === true. Sourcing the suggestions from the filtered
  // set hides labels that would silently match zero invokable workflows.
  const workflowLabelOptions = useMemo<MultiSelectAutocompleteOption[]>(() => {
    const seen = new Map<string, MultiSelectAutocompleteOption>();
    for (const w of apiPublishedWorkflows) {
      for (const l of w.labels ?? []) {
        if (!seen.has(l.name)) {
          seen.set(l.name, {
            value: l.name,
            label: l.name,
            description: l.description ?? undefined,
          });
        }
      }
    }
    return Array.from(seen.values()).sort((a, b) =>
      a.label.localeCompare(b.label),
    );
  }, [apiPublishedWorkflows]);

  const mcpToolOptions = useMemo<MultiSelectAutocompleteOption[]>(() => {
    const allowed: MultiSelectAutocompleteOption[] = [];
    for (const item of adminCatalog) {
      const access = orgNodeAccess[item.node_id];
      const orgHasOverrides = Object.keys(orgNodeAccess).length > 0;
      const visible = orgHasOverrides
        ? access?.allowed_by_capitol === true
        : true;
      if (!visible) continue;
      allowed.push({
        value: item.node_id,
        label: item.display_name || item.node_id,
        description: item.description || item.category || undefined,
      });
    }
    return allowed.sort((a, b) => a.label.localeCompare(b.label));
  }, [adminCatalog, orgNodeAccess]);

  const dataCollectionOptions = useMemo<MultiSelectAutocompleteOption[]>(
    () =>
      orgCollections
        .map((c) => ({
          value: c.id,
          label: c.name || c.id,
          description: c.description || undefined,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [orgCollections],
  );

  /**
   * Build a compact summary of one workflow's payload for the description
   * generator: name + description + distinct node ``type`` values + input
   * categories derived from input nodes (``*_input_node``).
   *
   * Returns ``null`` when the GET fails so the caller can keep going --
   * description generation should never block on a single flaky workflow.
   */
  const _summariseWorkflow = async (
    workflowId: string,
    orgId: string,
  ): Promise<WorkflowSummaryForDescription | null> => {
    try {
      const resp = await getWorkflow(workflowId, orgId);
      const inner = resp?.data?.workflow;
      const payload = inner?.payload;
      if (!payload) return null;

      const nodeKinds = new Set<string>();
      const inputKinds = new Set<string>();
      for (const n of payload.nodes ?? []) {
        const t = (n as { type?: string }).type;
        if (typeof t !== 'string' || !t) continue;
        nodeKinds.add(t);
        // Convention: nodes whose type ends in ``_input_node`` declare the
        // workflow's external input shape (file / string / enum / ...).
        // The prefix is the kind label.  E.g. ``file_input_node`` -> "file".
        if (t.endsWith('_input_node')) {
          const kind = t.slice(0, -'_input_node'.length);
          if (kind) inputKinds.add(kind);
        }
      }

      // Prefer the full row's name/description when present (the list view
      // always has them); fall back to the payload copy.
      const row = orgWorkflows.find((w) => w.workflow_id === workflowId);
      return {
        id: workflowId,
        name: row?.name || payload.name || '',
        description: row?.description || payload.description || '',
        node_kinds: Array.from(nodeKinds).sort(),
        input_kinds: Array.from(inputKinds).sort(),
      };
    } catch (e) {
      console.warn(
        `Failed to fetch workflow ${workflowId} for description summary:`,
        e,
      );
      return null;
    }
  };

  const handleGenerateDescription = async () => {
    if (!currentOrg?.orgid) return;
    setIsGeneratingDescription(true);
    setGenerateDescriptionError(null);
    try {
      // The org's workflow catalog gives us human-friendly names for the
      // ids the user picked - the LLM can cite them by display name.
      const workflowNames = workflowAllowlist
        .map((id) => orgWorkflows.find((w) => w.workflow_id === id)?.name)
        .filter((n): n is string => !!n);

      // When the operator ticks "Describe from workflows", fetch each
      // allowlisted workflow's payload in parallel and turn it into a
      // compact summary for the LLM.  Failures (per-workflow GET errors)
      // are swallowed -- best-effort, the caller still gets the names.
      let workflowSummaries: WorkflowSummaryForDescription[] | undefined;
      if (describeFromWorkflows && workflowAllowlist.length > 0) {
        const orgId = currentOrg.orgid;
        const settled = await Promise.all(
          workflowAllowlist.map((id) => _summariseWorkflow(id, orgId)),
        );
        workflowSummaries = settled.filter(
          (s): s is WorkflowSummaryForDescription => s !== null,
        );
      }

      const generated = await autoGenerateAgentDescription(currentOrg.orgid, {
        name,
        system_prompt: systemPrompt,
        model_name: modelName,
        workflow_allowlist: workflowAllowlist,
        workflow_allowlist_labels: workflowAllowlistLabels,
        mcp_tool_allowlist: mcpToolAllowlist,
        data_collection_allowlist: dataCollectionAllowlist,
        enable_workflow_runtime: enableWorkflowRuntime,
        enable_workflow_authoring: enableWorkflowAuthoring,
        enable_a2a_outbound: enableA2AOutbound,
        workflow_names: workflowNames,
        workflow_summaries: workflowSummaries,
      });
      if (generated) update('description', generated);
      else setGenerateDescriptionError('No description returned - try again.');
    } catch (e) {
      setGenerateDescriptionError(
        e instanceof Error ? e.message : 'Failed to generate description',
      );
    } finally {
      setIsGeneratingDescription(false);
    }
  };

  const handleSave = async () => {
    if (!currentOrg?.orgid || !name.trim()) return;
    setIsSaving(true);
    setError(null);
    try {
      const payload: AgentCreateRequest = {
        name: name.trim(),
        description,
        model_provider: modelProvider,
        model_name: modelName,
        system_prompt: systemPrompt,
        max_tokens: maxTokens,
        temperature,
        top_p: topP,
        thinking_enabled: thinkingEnabled,
        thinking_budget_tokens: thinkingBudgetTokens,
        enable_workflow_runtime: enableWorkflowRuntime,
        enable_workflow_authoring: enableWorkflowAuthoring,
        enable_a2a_outbound: enableA2AOutbound,
        enable_scheduling: enableScheduling,
        render_files_in_chat: renderFilesInChat,
        show_intermediate_files: showIntermediateFiles,
        allow_user_workflows: allowUserWorkflows,
        enable_github_tools: enableGithubTools,
        memory_enabled: memoryEnabled,
        memory_retention_days: memoryRetentionDays,
        exposed_via_a2a: exposedViaA2A,
        enable_task_alignment_guardrail: enableTaskAlignmentGuardrail,
        task_alignment_min_relevance: taskAlignmentMinRelevance,
        e2b_enabled: e2bEnabled,
        e2b_template: e2bTemplate.trim(),
        e2b_run_timeout_s: e2bRunTimeoutS,
        e2b_max_edit_calls_per_turn: e2bMaxEditCalls,
        e2b_allow_internet_access: e2bAllowInternet,
        e2b_sandbox_env: Object.fromEntries(
          e2bSandboxEnv
            .filter((entry) => entry.key.trim())
            .map((entry) => [entry.key.trim(), entry.value]),
        ),
        allow_synthetic_data_generation: !refuseSyntheticDataGeneration,
        workflow_allowlist: workflowAllowlist,
        workflow_allowlist_labels: workflowAllowlistLabels,
        mcp_tool_allowlist: mcpToolAllowlist,
        data_collection_allowlist: dataCollectionAllowlist,
      };

      // Reconcile the form + dirty baseline to the persisted (cleaned) values
      // so an unmodified save doesn't leave the form looking dirty.
      const persisted = cleanedFormSnapshot(currentSnapshot);

      if (isNew) {
        const created = await createAgent(currentOrg.orgid, payload);
        if (!created) {
          setError('Create failed');
          return;
        }
        // Surface the bearer once - we never get it back from a read.
        setRevealedBearer(created.a2a_bearer_token);
        setA2aEndpointUrl(created.a2a_endpoint_url);
        setA2aAgentCardUrl(created.a2a_agent_card_url);
        setLoadedAgent(created.agent);
        setForm(persisted);
        setSavedSnapshot(persisted);
        // Land on the Connection tab so the one-time bearer + snippets are
        // front and center right after creation.
        setActiveTab('connection');
        // Move URL to the edit shape so the user lands on the bearer-display
        // panel. The bearer is already in state and shown below.
        navigate(ROUTES.AGENTS.CONFIG.replace(':agentId', created.agent.id), {
          replace: true,
        });
      } else if (agentId) {
        const updated = await updateAgent(currentOrg.orgid, agentId, payload);
        if (!updated || !updated.agent) {
          setError('Update failed');
          return;
        }
        setLoadedAgent(updated.agent);
        setForm(persisted);
        setSavedSnapshot(persisted);
        // Refresh the connection panel URLs in case the public-URL env
        // changed since the page loaded (very unlikely, but cheap).
        if (updated.a2a_endpoint_url)
          setA2aEndpointUrl(updated.a2a_endpoint_url);
        if (updated.a2a_agent_card_url)
          setA2aAgentCardUrl(updated.a2a_agent_card_url);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setIsSaving(false);
    }
  };

  // BearersPanel owns the bearer flow and its own clipboard plumbing; this
  // helper only serves the IntegrationSnippets copy buttons.
  const copyToClipboard = (text: string) => {
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      navigator.clipboard.writeText(text);
    }
  };

  if (isLoading) {
    return (
      <div className='flex items-center justify-center py-16'>
        <Loader2 className='h-6 w-6 animate-spin' />
      </div>
    );
  }

  return (
    <div className='max-w-3xl mx-auto p-6 space-y-6'>
      <div className='flex items-center justify-between gap-3'>
        <h1 className='text-2xl font-semibold truncate'>
          {isNew ? 'New Agent' : `Configure ${loadedAgent?.name || 'Agent'}`}
        </h1>
        <div className='flex shrink-0 items-center gap-2'>
          {!isNew && loadedAgent && (
            <Button
              variant='outline'
              onClick={() =>
                navigate(ROUTES.AGENTS.CHAT.replace(':agentId', loadedAgent.id))
              }
            >
              Chat with this agent
            </Button>
          )}
          <Button
            variant='outline'
            onClick={() =>
              navigate(
                isNew
                  ? ROUTES.AGENTS.ROOT
                  : ROUTES.AGENTS.CHAT.replace(':agentId', agentId || ''),
              )
            }
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={isSaving || !name.trim() || !currentOrg?.orgid}
          >
            <Save className='mr-2 size-4' />
            {isSaving ? 'Saving...' : isNew ? 'Create Agent' : 'Save Changes'}
          </Button>
        </div>
      </div>

      {error && (
        <div className='rounded border border-destructive bg-destructive/10 p-3 text-sm text-destructive'>
          {error}
        </div>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value='configuration'>Configuration</TabsTrigger>
          <TabsTrigger value='connection' disabled={isNew || !loadedAgent}>
            Connection
          </TabsTrigger>
        </TabsList>

        <TabsContent value='configuration' className='mt-6 space-y-6'>
          {/* === Identity === */}
          <section className='space-y-3'>
            <h2 className='text-sm font-medium text-muted-foreground uppercase tracking-wide'>
              Identity
            </h2>
            <div className='space-y-2'>
              <FieldLabel
                htmlFor='name'
                label='Name'
                help='Display name shown in the agents list and on the public A2A AgentCard. Visible to external orchestrators that discover this agent.'
              />
              <Input
                id='name'
                value={name}
                onChange={(e) => update('name', e.target.value)}
                maxLength={100}
                placeholder='e.g. Capitol Orchestrator for EY'
              />
            </div>
            <div className='space-y-2'>
              <div className='flex items-center justify-between'>
                <FieldLabel
                  htmlFor='description'
                  label='Description'
                  help='Short description of what the agent does. Surfaced in the AgentCard description field that A2A peers use to decide whether to call this agent.'
                />
                <div className='flex items-center gap-3'>
                  <TooltipProvider delayDuration={150}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <label className='flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer select-none'>
                          <Checkbox
                            id='describe-from-workflows'
                            checked={describeFromWorkflows}
                            onCheckedChange={(v) =>
                              setDescribeFromWorkflows(v === true)
                            }
                            disabled={
                              isGeneratingDescription ||
                              workflowAllowlist.length === 0
                            }
                            className='h-3.5 w-3.5'
                          />
                          Describe from workflows
                        </label>
                      </TooltipTrigger>
                      <TooltipContent
                        side='left'
                        className='max-w-xs text-xs leading-snug'
                      >
                        {workflowAllowlist.length === 0
                          ? 'Add workflows to the allowlist below to enable this option.'
                          : 'When checked, the generator inspects each allowlisted workflow\u2019s graph (node kinds + input shape) so the description can describe what the agent can DO with the workflows, not just list them by name.'}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  <TooltipProvider delayDuration={150}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          type='button'
                          variant='outline'
                          size='sm'
                          onClick={handleGenerateDescription}
                          disabled={
                            isGeneratingDescription || !currentOrg?.orgid
                          }
                          className='h-7 gap-1.5 text-xs'
                        >
                          {isGeneratingDescription ? (
                            <Loader2 className='h-3.5 w-3.5 animate-spin' />
                          ) : (
                            <Wand2 className='h-3.5 w-3.5' />
                          )}
                          {isGeneratingDescription
                            ? 'Generating\u2026'
                            : 'Auto-generate'}
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent
                        side='left'
                        className='max-w-xs text-xs leading-snug'
                      >
                        Synthesize a description from this agent&apos;s name,
                        system prompt, allowlisted workflows, labels, tools, and
                        capability flags. Tick &quot;Describe from
                        workflows&quot; first to also pull in each
                        workflow&apos;s graph + input shape. You can edit the
                        result.
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              </div>
              <Textarea
                id='description'
                value={description}
                onChange={(e) => update('description', e.target.value)}
                maxLength={500}
                rows={3}
                placeholder='What does this agent do? Click Auto-generate to synthesize from the agent metadata below.'
              />
              {generateDescriptionError && (
                <p className='text-xs text-destructive'>
                  {generateDescriptionError}
                </p>
              )}
            </div>
          </section>

          {/* === Model & prompt === */}
          <section className='space-y-3'>
            <h2 className='text-sm font-medium text-muted-foreground uppercase tracking-wide'>
              Model
            </h2>
            <div className='space-y-2'>
              <FieldLabel
                label='Model'
                help='LLM that powers the agent. Per-model output caps and thinking-mode availability are enforced automatically.'
              />
              <Select
                value={modelName}
                onValueChange={(v) => {
                  update('modelName', v);
                  const opt = AGENT_MODELS.find((m) => m.value === v);
                  if (opt) {
                    update('modelProvider', opt.provider);
                    // Clamp max tokens to the new model's cap so save doesn't 400.
                    if (maxTokens > opt.maxOutputTokens) {
                      update('maxTokens', opt.maxOutputTokens);
                    }
                    // Auto-disable thinking when switching to a non-thinking model.
                    if (!opt.supportsThinking && thinkingEnabled) {
                      update('thinkingEnabled', false);
                    }
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {AGENT_MODELS.map((m) => (
                    <SelectItem key={m.value} value={m.value}>
                      <div className='flex items-center gap-2'>
                        <span>{m.label}</span>
                        <span className='text-xs text-muted-foreground'>
                          {(m.maxOutputTokens / 1000).toFixed(0)}K out
                          {m.supportsThinking ? ' · thinking' : ''}
                        </span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className='space-y-2'>
              <FieldLabel
                htmlFor='system_prompt'
                label='System prompt'
                help='Instructions injected at the top of every conversation. Defines the agent persona, ground rules, and any baked-in policy. Shown to the model before any user message.'
              />
              <Textarea
                id='system_prompt'
                value={systemPrompt}
                onChange={(e) => update('systemPrompt', e.target.value)}
                rows={6}
                className='font-mono text-xs'
              />
            </div>

            <div className='grid grid-cols-1 md:grid-cols-3 gap-3'>
              <div className='space-y-2'>
                <FieldLabel
                  htmlFor='temperature'
                  label='Temperature'
                  help='Sampling creativity. 0 is deterministic; ~0.7 is a good default for conversational agents; 1.0+ is creative but inconsistent. Range 0-2.'
                />
                <Input
                  id='temperature'
                  type='number'
                  step='0.1'
                  min='0'
                  max='2'
                  value={temperature}
                  onChange={(e) =>
                    update('temperature', parseFloat(e.target.value) || 0)
                  }
                />
              </div>
              <div className='space-y-2'>
                <FieldLabel
                  htmlFor='top_p'
                  label='Top P'
                  help='Nucleus sampling cutoff: only sample from tokens whose cumulative probability is ≤ P. 1.0 disables it. Most users should keep this at 1.0 and tune temperature instead.'
                />
                <Input
                  id='top_p'
                  type='number'
                  step='0.05'
                  min='0'
                  max='1'
                  value={topP}
                  onChange={(e) =>
                    update('topP', parseFloat(e.target.value) || 0)
                  }
                />
              </div>
              <div className='space-y-2'>
                <FieldLabel
                  htmlFor='max_tokens'
                  label='Max output tokens'
                  help={`Maximum number of tokens the model may generate per turn (output only - input/context is bounded separately by the model's window). Selected model caps at ${currentModelMaxOutput.toLocaleString()} tokens.`}
                />
                <Input
                  id='max_tokens'
                  type='number'
                  min='1'
                  max={currentModelMaxOutput}
                  value={maxTokens}
                  onChange={(e) =>
                    update('maxTokens', parseInt(e.target.value, 10) || 1)
                  }
                />
                <p className='text-[11px] text-muted-foreground'>
                  Cap for selected model:{' '}
                  {currentModelMaxOutput.toLocaleString()}
                </p>
              </div>
            </div>

            {/* Extended thinking */}
            <div className='space-y-3 rounded-md border border-border/60 bg-muted/30 p-3'>
              <div className='flex items-start justify-between gap-3'>
                <div className='space-y-0.5'>
                  <FieldLabel
                    label='Extended thinking'
                    help='When enabled, the model produces a "thinking" trace before the final answer (visible in chain-of-thought output). Improves answer quality on hard reasoning tasks at the cost of latency and tokens. Requires a thinking-capable model.'
                  />
                  <p className='text-xs text-muted-foreground'>
                    {currentModelSupportsThinking
                      ? 'Available on the selected model.'
                      : 'Not available on the selected model.'}
                  </p>
                </div>
                <Switch
                  checked={thinkingEnabled && currentModelSupportsThinking}
                  onCheckedChange={(v) => update('thinkingEnabled', v)}
                  disabled={!currentModelSupportsThinking}
                />
              </div>
              {thinkingEnabled && currentModelSupportsThinking && (
                <div className='space-y-2'>
                  <FieldLabel
                    htmlFor='thinking_budget'
                    label='Thinking budget'
                    help='Maximum tokens the model may spend on the thinking trace. Must be < Max output tokens. Higher = deeper reasoning but slower / more expensive. 8K is a balanced default; bump to 32K+ for hard analytical tasks.'
                  />
                  <Input
                    id='thinking_budget'
                    type='number'
                    min={1024}
                    max={Math.min(128_000, maxTokens - 1)}
                    step={1024}
                    value={thinkingBudgetTokens}
                    onChange={(e) =>
                      update(
                        'thinkingBudgetTokens',
                        parseInt(e.target.value, 10) || 1024,
                      )
                    }
                  />
                  {thinkingBudgetTokens >= maxTokens && (
                    <p className='text-[11px] text-destructive'>
                      Thinking budget must be less than Max output tokens (
                      {maxTokens.toLocaleString()}).
                    </p>
                  )}
                </div>
              )}
            </div>
          </section>

          {/* === Capability flags === */}
          <section className='space-y-3'>
            <h2 className='text-sm font-medium text-muted-foreground uppercase tracking-wide'>
              Capabilities
            </h2>
            <CapabilityToggle
              label='Workflow runtime tools'
              help='list / start / await / stop / pause / resume workflows'
              value={enableWorkflowRuntime}
              onChange={(v) => update('enableWorkflowRuntime', v)}
            />
            <CapabilityToggle
              label='Allow user workflows'
              help="union each caller's own and shared workflows into this agent's effective workflow set; when off, everyone sees exactly the configured allowlist"
              value={allowUserWorkflows}
              onChange={(v) => update('allowUserWorkflows', v)}
            />
            <CapabilityToggle
              label='Per-user memory'
              help='remember standing instructions each user saves in chat ("always use context graphs") across sessions; when off, no memory tools attach and nothing is stored'
              value={memoryEnabled}
              onChange={(v) => update('memoryEnabled', v)}
            />
            {memoryEnabled && (
              <div className='space-y-2 rounded-lg border p-3 ml-6'>
                <FieldLabel
                  htmlFor='memory_retention_days'
                  label='Memory retention (days)'
                  help='Auto-expire saved memories older than this many days; expired entries are physically deleted on the next read. 0 keeps them until the user forgets them.'
                />
                <Input
                  id='memory_retention_days'
                  type='number'
                  min='0'
                  max='3650'
                  value={memoryRetentionDays}
                  onChange={(e) =>
                    update(
                      'memoryRetentionDays',
                      Math.max(
                        0,
                        Math.min(3650, parseInt(e.target.value, 10) || 0),
                      ),
                    )
                  }
                />
                <p className='text-[11px] text-muted-foreground'>
                  0 = keep forever. Max 3650 (10 years).
                </p>
              </div>
            )}
            <CapabilityToggle
              label='Workflow authoring tools'
              help='build / edit / lint / publish workflows via the workflow-builder MCP'
              value={enableWorkflowAuthoring}
              onChange={(v) => update('enableWorkflowAuthoring', v)}
            />
            <CapabilityToggle
              label='GitHub tools'
              help='let this agent create and list GitHub software repos and attach them to workflows (create_software_repo / list_software_repos / attach_software_repo_to_workflow); requires the org GitHub credential, and repo attachment only works for the workflow owner'
              value={enableGithubTools}
              onChange={(v) => update('enableGithubTools', v)}
            />
            <CapabilityToggle
              label='Refuse synthetic data generation'
              help='when on (default), this orchestrator never offers to invent sample or placeholder files — the user must upload a real file. turn off only if an administrator wants the consent-gated create_artifact offer'
              value={refuseSyntheticDataGeneration}
              onChange={(v) => update('refuseSyntheticDataGeneration', v)}
            />
            <CapabilityToggle
              label='Workflow scheduling'
              help='let this agent schedule allowlisted workflows to run later — on a cron cadence, or once at a set time. scheduled runs need the same per-workflow run permission as running one now'
              value={enableScheduling}
              onChange={(v) => update('enableScheduling', v)}
            />
            <CapabilityToggle
              label='A2A outbound (call peer agents)'
              help='allow this agent to call out to external A2A peer agents'
              value={enableA2AOutbound}
              onChange={(v) => update('enableA2AOutbound', v)}
            />
            <CapabilityToggle
              label='Exposed via A2A inbound'
              help='if disabled, the agent card / JSON-RPC routes return 401 (kill switch)'
              value={exposedViaA2A}
              onChange={(v) => update('exposedViaA2A', v)}
            />
            <CapabilityToggle
              label='Render files in chat'
              help='when off, file links and previews are never streamed into chat replies; clients render files from workflow.files_available events instead (for external consumers with their own file UI)'
              value={renderFilesInChat}
              onChange={(v) => update('renderFilesInChat', v)}
            />
            <CapabilityToggle
              label='Show intermediate files'
              help='default display verdict for intermediate (non-primary) files on workflow.files_available events; when off, interim files carry display:false until a user opts in per conversation'
              value={showIntermediateFiles}
              onChange={(v) => update('showIntermediateFiles', v)}
            />
            <CapabilityToggle
              label='Relevance guardrail'
              help="score a task amended in the chat against this agent's purpose and refuse to run it if it isn't relevant (off by default)"
              value={enableTaskAlignmentGuardrail}
              onChange={(v) => update('enableTaskAlignmentGuardrail', v)}
            />
            {enableTaskAlignmentGuardrail && (
              <div className='space-y-2 rounded-lg border p-3 ml-6'>
                <FieldLabel
                  htmlFor='task_alignment_min_relevance'
                  label='Relevance threshold'
                  help="An amended task is scored 0-100 for how well it matches this agent's purpose; a task scoring below this value is refused before the workflow runs. Higher = stricter."
                />
                <Input
                  id='task_alignment_min_relevance'
                  type='number'
                  min='0'
                  max='100'
                  value={taskAlignmentMinRelevance}
                  onChange={(e) =>
                    update(
                      'taskAlignmentMinRelevance',
                      Math.max(
                        0,
                        Math.min(100, parseInt(e.target.value, 10) || 0),
                      ),
                    )
                  }
                />
                <p className='text-[11px] text-muted-foreground'>
                  0-100. Tasks scoring below this are refused before the
                  workflow runs.
                </p>
              </div>
            )}
          </section>

          {/* === Code execution (E2B) === */}
          <section className='space-y-3'>
            <h2 className='text-sm font-medium text-muted-foreground uppercase tracking-wide'>
              Code execution (E2B sandbox)
            </h2>
            <CapabilityToggle
              label='Code execution'
              help='let this agent create and edit files by running code in an isolated E2B sandbox (create_artifact / edit_artifact tools)'
              value={e2bEnabled}
              onChange={(v) => update('e2bEnabled', v)}
            />
            {e2bEnabled && (
              <div className='space-y-4 rounded-lg border p-3 ml-6'>
                <div className='flex items-start justify-between gap-3'>
                  <div className='space-y-0.5'>
                    <FieldLabel
                      label='Internet access'
                      help='Allow network egress from the sandbox while agent code runs. Turn off for agents that process sensitive data — code can then only work with the files staged into the sandbox.'
                    />
                    <p className='text-xs text-muted-foreground'>
                      Off = sandbox code cannot reach the network.
                    </p>
                  </div>
                  <Switch
                    checked={e2bAllowInternet}
                    onCheckedChange={(v) => update('e2bAllowInternet', v)}
                  />
                </div>

                <div className='grid grid-cols-1 gap-4 sm:grid-cols-3'>
                  <div className='space-y-2'>
                    <FieldLabel
                      htmlFor='e2b_template'
                      label='Sandbox template'
                      help="Force a specific E2B template tag for every sandbox this agent creates. Sandbox size (CPU/RAM) and pre-installed packages are properties of the template, so pointing at a larger custom template is also how you get a bigger sandbox. Empty = the deployment's default template (one unified image already covers documents, spreadsheets, and code)."
                    />
                    <Input
                      id='e2b_template'
                      placeholder='default'
                      maxLength={64}
                      value={e2bTemplate}
                      onChange={(e) => update('e2bTemplate', e.target.value)}
                    />
                  </div>
                  <div className='space-y-2'>
                    <FieldLabel
                      htmlFor='e2b_run_timeout_s'
                      label='Run timeout (s)'
                      help='Wall-clock cap per code run inside the sandbox, clamped to 10-900s server-side. 0 keeps the deployment defaults (120s create / 180s edit).'
                    />
                    <Input
                      id='e2b_run_timeout_s'
                      type='number'
                      min='0'
                      max='900'
                      value={e2bRunTimeoutS}
                      onChange={(e) =>
                        update(
                          'e2bRunTimeoutS',
                          Math.max(
                            0,
                            Math.min(900, parseInt(e.target.value, 10) || 0),
                          ),
                        )
                      }
                    />
                    <p className='text-[11px] text-muted-foreground'>
                      0 = deployment default
                    </p>
                  </div>
                  <div className='space-y-2'>
                    <FieldLabel
                      htmlFor='e2b_max_edit_calls'
                      label='Edits per turn'
                      help='Maximum edit_artifact calls in one chat turn (each call costs sandbox time and storage I/O), clamped to 1-20 server-side. 0 keeps the deployment default (5).'
                    />
                    <Input
                      id='e2b_max_edit_calls'
                      type='number'
                      min='0'
                      max='20'
                      value={e2bMaxEditCalls}
                      onChange={(e) =>
                        update(
                          'e2bMaxEditCalls',
                          Math.max(
                            0,
                            Math.min(20, parseInt(e.target.value, 10) || 0),
                          ),
                        )
                      }
                    />
                    <p className='text-[11px] text-muted-foreground'>
                      0 = deployment default
                    </p>
                  </div>
                </div>

                <div className='space-y-2'>
                  <FieldLabel
                    label='Sandbox environment variables'
                    help='Injected into every sandbox this agent creates, so generation/edit code can reach your own APIs. Up to 20 entries; keys must be valid env var names (letters, digits, underscores); values are truncated at 512 characters server-side.'
                  />
                  {e2bSandboxEnv.map((entry, index) => (
                    <div key={index} className='flex items-center gap-2'>
                      <Input
                        placeholder='KEY'
                        className='w-56 font-mono text-xs'
                        value={entry.key}
                        onChange={(e) =>
                          update('e2bSandboxEnv', (prev) =>
                            prev.map((row, i) =>
                              i === index
                                ? { ...row, key: e.target.value }
                                : row,
                            ),
                          )
                        }
                      />
                      <Input
                        placeholder='value'
                        className='flex-1 font-mono text-xs'
                        value={entry.value}
                        onChange={(e) =>
                          update('e2bSandboxEnv', (prev) =>
                            prev.map((row, i) =>
                              i === index
                                ? { ...row, value: e.target.value }
                                : row,
                            ),
                          )
                        }
                      />
                      <Button
                        variant='ghost'
                        size='icon'
                        className='size-8 shrink-0'
                        aria-label='Remove environment variable'
                        onClick={() =>
                          update('e2bSandboxEnv', (prev) =>
                            prev.filter((_, i) => i !== index),
                          )
                        }
                      >
                        <X className='size-4' />
                      </Button>
                    </div>
                  ))}
                  <Button
                    variant='outline'
                    size='sm'
                    disabled={e2bSandboxEnv.length >= 20}
                    onClick={() =>
                      update('e2bSandboxEnv', (prev) => [
                        ...prev,
                        { key: '', value: '' },
                      ])
                    }
                  >
                    <Plus className='size-4' />
                    Add variable
                  </Button>
                </div>
              </div>
            )}
          </section>

          {/* === Allowlists ===
          Workflow id + label fields autocomplete from the org's workflow
          catalog (we pull the list once on page load). MCP tools and data
          collections are free-text-only for v1 - the data model already
          accepts the structured shape; the catalog wiring is deferred to a
          follow-up so we don't block agent creation on it.
      */}
          <section className='space-y-3'>
            <h2 className='text-sm font-medium text-muted-foreground uppercase tracking-wide'>
              Allowlists
            </h2>
            <AllowlistRow
              label='Workflow allowlist (workflow ids)'
              help='Empty = all API-published workflows in this org. Only workflows with the "Publish to API" flag are searchable; you can still paste any id.'
              value={workflowAllowlist}
              onChange={(v) => update('workflowAllowlist', v)}
              options={workflowIdOptions}
              placeholder='Type a workflow name or paste an id…'
              emptyMessage='No API-published workflows in this org yet.'
            />
            <AllowlistRow
              label='Workflow allowlist labels'
              help='e.g. ey-production. Only labels carried by API-published workflows are searchable; matched workflows must also have the "Publish to API" flag on to be exposed.'
              value={workflowAllowlistLabels}
              onChange={(v) => update('workflowAllowlistLabels', v)}
              options={workflowLabelOptions}
              placeholder='Type a label or pick from existing tags…'
              emptyMessage='No labels on API-published workflows in this org yet.'
            />
            <AllowlistRow
              label='MCP tool allowlist'
              help='MCP tool names from the org-nodes catalog the agent may use'
              value={mcpToolAllowlist}
              onChange={(v) => update('mcpToolAllowlist', v)}
              options={mcpToolOptions}
              placeholder='Type a tool name or pick from the catalog…'
              emptyMessage='No tools in this org yet.'
            />
            <AllowlistRow
              label='Data collection allowlist'
              help='Collection ids from data-sources the agent may retrieve from'
              value={dataCollectionAllowlist}
              onChange={(v) => update('dataCollectionAllowlist', v)}
              options={dataCollectionOptions}
              placeholder='Type a collection name or paste an id…'
              emptyMessage='No collections in this org yet.'
            />
          </section>
        </TabsContent>

        <TabsContent value='connection' className='mt-6 space-y-6'>
          {/* === A2A connection === */}
          {!isNew && loadedAgent && (
            <section className='space-y-3'>
              <h2 className='text-sm font-medium text-muted-foreground uppercase tracking-wide'>
                A2A Connection
              </h2>
              <div className='border rounded p-4 space-y-3 bg-muted/30'>
                {/* URLs come from platform-api (which owns AGENTIC_BACKEND_PUBLIC_URL).
                Only fall through to a client-side fallback when the API didn't
                return them (older platform-api version) - and only if we can
                infer a same-origin substitution. Default to "(unavailable)" so
                we never surface a wrong URL that 404s. */}
                <ConnectionRow
                  label='Agent card URL'
                  value={
                    a2aAgentCardUrl || '(unavailable - upgrade platform-api)'
                  }
                  onCopy={copyToClipboard}
                />
                <ConnectionRow
                  label='JSON-RPC endpoint'
                  value={
                    a2aEndpointUrl || '(unavailable - upgrade platform-api)'
                  }
                  onCopy={copyToClipboard}
                />
                {/* canMutate mirrors the Save / Delete gate in the agent
                header: the route itself is permission-gated, so there is no
                separate per-bearer auth. */}
                {currentOrg?.orgid && (
                  <BearersPanel
                    orgId={currentOrg.orgid}
                    agentId={loadedAgent.id}
                    canMutate={!isSaving}
                  />
                )}
                {loadedAgent.runtime_canvas_workflow_id && (
                  <div className='pt-2 border-t border-border/50'>
                    <Button
                      variant='link'
                      className='p-0 h-auto'
                      onClick={() =>
                        navigate(
                          ROUTES.WORKFLOW.EDITOR.replace(
                            ':workflowId',
                            loadedAgent.runtime_canvas_workflow_id || '',
                          ),
                        )
                      }
                    >
                      Edit advanced canvas
                      <ExternalLink className='h-3 w-3 ml-1' />
                    </Button>
                  </div>
                )}
              </div>
            </section>
          )}

          {/* === Try it: copy-pasteable validation snippets === */}
          {!isNew && loadedAgent && (
            <IntegrationSnippets
              endpointUrl={a2aEndpointUrl}
              agentCardUrl={a2aAgentCardUrl}
              bearerForExamples={
                revealedBearer || `cap_a2a_<paste-your-bearer-here>`
              }
              bearerIsLive={!!revealedBearer}
              onCopy={copyToClipboard}
            />
          )}
        </TabsContent>
      </Tabs>

      {!isNew && (
        <PendingChangesFooter
          changesCount={changesCount}
          onDiscard={handleDiscard}
          onReview={handleSave}
          reviewLabel={isSaving ? 'Saving...' : 'Save changes'}
          description='Your edits are not saved yet'
          disabled={isSaving || !name.trim()}
        />
      )}
    </div>
  );
}

interface CapabilityToggleProps {
  label: string;
  help: string;
  value: boolean;
  onChange: (value: boolean) => void;
}

function CapabilityToggle({
  label,
  help,
  value,
  onChange,
}: CapabilityToggleProps) {
  return (
    <div className='flex items-start justify-between gap-3 rounded-lg border p-3 hover:bg-muted/30'>
      <div>
        <div className='text-sm font-medium'>{label}</div>
        <div className='text-xs text-muted-foreground mt-0.5'>{help}</div>
      </div>
      <Switch
        checked={value}
        onCheckedChange={onChange}
        className='mt-0.5 shrink-0'
      />
    </div>
  );
}

interface FieldLabelProps {
  label: string;
  help: string;
  htmlFor?: string;
  className?: string;
}

/** Inline label with a small "?" tooltip trigger to the right of the text.
 *
 * Use this for any agent-config form field so the operator can hover for a
 * one-liner explanation (input vs output tokens, what top-p does, etc.) without
 * having to read the platform docs. Adds the tooltip via the platform's
 * Radix-based primitive so dark mode / aria are correct out of the box.
 */
function FieldLabel({ label, help, htmlFor, className }: FieldLabelProps) {
  return (
    <div className={'flex items-center gap-1.5 ' + (className ?? '')}>
      <Label htmlFor={htmlFor} className='text-sm'>
        {label}
      </Label>
      <TooltipProvider delayDuration={150}>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type='button'
              tabIndex={-1}
              className='text-muted-foreground hover:text-foreground transition-colors cursor-help'
              aria-label={`${label} help`}
            >
              <HelpCircle className='size-3.5' />
            </button>
          </TooltipTrigger>
          <TooltipContent side='top' className='max-w-xs text-xs leading-snug'>
            {help}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
}

interface AllowlistRowProps {
  label: string;
  help: string;
  value: string[];
  onChange: (next: string[]) => void;
  options?: MultiSelectAutocompleteOption[];
  placeholder?: string;
  emptyMessage?: string;
}

function AllowlistRow({
  label,
  help,
  value,
  onChange,
  options,
  placeholder,
  emptyMessage,
}: AllowlistRowProps) {
  return (
    <div className='space-y-1'>
      <FieldLabel label={label} help={help} />
      <p className='text-xs text-muted-foreground'>{help}</p>
      <MultiSelectAutocomplete
        value={value}
        onChange={onChange}
        options={options}
        placeholder={placeholder}
        emptyMessage={emptyMessage}
      />
    </div>
  );
}

interface ConnectionRowProps {
  label: string;
  value: string;
  onCopy: (text: string) => void;
}

function ConnectionRow({ label, value, onCopy }: ConnectionRowProps) {
  return (
    <div className='space-y-1'>
      <Label className='text-xs text-muted-foreground'>{label}</Label>
      <div className='flex items-center gap-2'>
        <code className='flex-1 text-xs px-2 py-1 rounded bg-background border break-all'>
          {value}
        </code>
        <Button size='sm' variant='ghost' onClick={() => onCopy(value)}>
          <Copy className='h-3 w-3' />
        </Button>
      </div>
    </div>
  );
}
